import React, { useEffect, useRef, useState } from 'react';
import { mathService } from '../../services/math';
import { MathRenderOptions } from '../../services/math/types';
import { mathRenderCache } from '../../services/math/MathRenderCache';

interface MathRendererProps {
  content: string;
  className?: string;
  displayMode?: boolean;
  options?: MathRenderOptions;
}

/**
 * 新版数学公式渲染组件 - 智能显示模式
 * 支持 KaTeX 和自定义渲染器的降级方案
 * 智能判断数学公式是否应该行内显示
 */
export const MathRenderer: React.FC<MathRendererProps> = ({
  content,
  className = '',
  displayMode = false,
  options = {}
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * 智能判断数学公式是否应该行内显示 (带缓存优化)
   */
  const shouldDisplayInline = async (formula: string): Promise<boolean> => {
    // 检查缓存
    const cachedDisplayMode = mathRenderCache.getDisplayMode(formula);
    if (cachedDisplayMode !== null) {
      return cachedDisplayMode;
    }

    try {
      // 创建临时元素来测量渲染高度
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.visibility = 'hidden';
      tempDiv.style.fontSize = '14px'; // 与消息文本相同的字体大小
      tempDiv.style.lineHeight = '1.5';
      tempDiv.style.top = '-9999px';
      tempDiv.style.left = '-9999px';
      document.body.appendChild(tempDiv);

      // 渲染数学公式到临时元素
      await mathService.renderToElement(formula, tempDiv, { displayMode: false });

      // 获取渲染高度
      const mathHeight = tempDiv.offsetHeight;

      // 计算文本行高 (14px * 1.5 = 21px)
      const textLineHeight = 14 * 1.5;

      // 清理临时元素
      document.body.removeChild(tempDiv);

      // 如果数学公式高度不超过2倍文本行高，则行内显示
      const isInline = mathHeight <= textLineHeight * 2;

      // 缓存结果 (暂时只缓存显示模式，渲染结果在后面缓存)
      return isInline;
    } catch (error) {
      console.warn('Failed to measure math formula height:', error);
      // 降级：根据公式复杂度简单判断
      const isInline = !formula.includes('\\frac') && !formula.includes('\\sqrt') &&
        !formula.includes('\\sum') && !formula.includes('\\int') &&
        !formula.includes('\\prod') && !formula.includes('\\lim') &&
        !formula.includes('\\binom') && !formula.includes('\\matrix') &&
        formula.length < 20;

      return isInline;
    }
  };

  /**
   * 带缓存的数学公式渲染
   */
  const renderFormulaWithCache = async (formula: string, element: HTMLElement, renderOptions: MathRenderOptions): Promise<void> => {


    // 检查渲染结果缓存
    const cachedHTML = mathRenderCache.getRenderedHTML(formula, renderOptions);
    if (cachedHTML) {
      element.innerHTML = cachedHTML;
      return;
    }

    try {
      // 渲染并缓存结果
      await mathService.renderToElement(formula, element, renderOptions);



      // 缓存渲染结果
      const displayMode = renderOptions.displayMode || false;
      mathRenderCache.setCache(formula, !displayMode, element.innerHTML, renderOptions);
    } catch (error) {
      throw error;
    }
  };

  /**
   * 批量异步渲染公式 - 解决主线程阻塞问题
   */
  const renderFormulasInBatches = async (formulas: Array<{
    formula: string;
    element: HTMLElement;
    options: MathRenderOptions;
    needsDisplayModeCheck?: boolean;
  }>, batchSize = 10): Promise<void> => {
    for (let i = 0; i < formulas.length; i += batchSize) {
      const batch = formulas.slice(i, i + batchSize);

      // 并行处理一批公式
      await Promise.all(batch.map(async ({ formula, element, options, needsDisplayModeCheck }) => {
        try {
          if (needsDisplayModeCheck) {
            // 需要智能判断显示模式的公式
            const isInline = await shouldDisplayInline(formula);
            const finalOptions = { ...options, displayMode: !isInline };
            await renderFormulaWithCache(formula, element, finalOptions);
          } else {
            // 直接渲染的公式
            await renderFormulaWithCache(formula, element, options);
          }
        } catch (mathError) {
          console.warn('Math formula rendering failed:', mathError);
          element.innerHTML = `<span class="math-error">${formula}</span>`;
        }
      }));

      // 让出控制权给浏览器，保持界面响应 - 使用requestAnimationFrame
      if (i + batchSize < formulas.length) {
        await new Promise(resolve => requestAnimationFrame(resolve));
      }
    }
  };

  /**
   * 处理混合内容（文本 + 数学公式）
   */
  const processContent = (text: string): string => {
    // 保持原始换行符，让 CSS white-space: pre-wrap 处理
    let processed = text;

    // 清理HTML标签，但保留换行
    processed = processed
      .replace(/<strong>(.*?)<\/strong>/g, '**$1**') // 转换为Markdown粗体
      .replace(/<br\s*\/?>/g, '\n') // 转换HTML换行为文本换行
      .replace(/<\/?(div|p|span)[^>]*>/g, '') // 移除其他HTML标签
      .replace(/&nbsp;/g, ' ') // 转换HTML空格
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');



    // 处理LaTeX块级数学公式 \[...\]
    processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, (_, formula) => {
      return `<div class="math-display-block">${formula.trim()}</div>`;
    });

    // 处理LaTeX行内数学公式 \(...\)
    processed = processed.replace(/\\\((.*?)\\\)/g, (_, formula) => {
      return `<span class="math-inline-placeholder" data-formula="${formula.trim()}">${formula.trim()}</span>`;
    });

    // 处理块级数学公式 $$...$$
    processed = processed.replace(/\$\$(.*?)\$\$/gs, (_, formula) => {
      return `<div class="math-display-block">${formula.trim()}</div>`;
    });

    // 处理行内数学公式 $...$ - 先标记，稍后智能判断
    processed = processed.replace(/\$(.*?)\$/g, (_, formula) => {
      return `<span class="math-inline-placeholder" data-formula="${formula.trim()}">${formula.trim()}</span>`;
    });

    // 处理双反引号包裹的数学表达式 ``...`` - 优先处理
    processed = processed.replace(/``([^`]+)``/g, (match, content) => {
      const trimmedContent = content.trim();

      // 双反引号内的内容，如果包含数学符号就当作数学公式处理
      const mathPatterns = [
        /\\[a-zA-Z]+/, // LaTeX命令 (\gamma, \frac, \sqrt等)
        /[α-ωΑ-Ω]/, // 希腊字母
        /[∑∫∏∂∇∆]/, // 数学符号
        /\^|\{|\}/, // 上标和大括号
        /[=≠<>≤≥±∓×÷]/, // 数学运算符
        /dp|dt|ds|dE/, // 微分符号
        /\b[a-zA-Z]\s*=/, // 变量赋值 (p =, E =等)
        /\/.*\//, // 分数形式 (v/c等)
      ];

      const isMath = mathPatterns.some(pattern => pattern.test(trimmedContent));

      if (isMath) {
        // 是数学表达式，标记为数学公式
        return `<span class="math-inline-placeholder" data-formula="${trimmedContent}">${trimmedContent}</span>`;
      } else {
        // 是普通代码，保持原样
        return `<code class="inline-code">${trimmedContent}</code>`;
      }
    });

    // 处理单反引号包裹的数学表达式 `...` - 检测是否包含数学符号
    processed = processed.replace(/`([^`]+)`/g, (match, content) => {
      const trimmedContent = content.trim();

      // 检测是否包含数学符号或希腊字母
      const mathPatterns = [
        /\\[a-zA-Z]+/, // LaTeX命令 (\gamma, \frac, \sqrt等)
        /[α-ωΑ-Ω]/, // 希腊字母
        /[∑∫∏∂∇∆]/, // 数学符号
        /\^|\{|\}/, // 上标和大括号
        /[=≠<>≤≥±∓×÷]/, // 数学运算符
        /dp|dt|ds|dE/, // 微分符号
        /\b[a-zA-Z]\s*=/, // 变量赋值 (p =, E =等)
        /\/.*\//, // 分数形式 (v/c等)
      ];

      const isMath = mathPatterns.some(pattern => pattern.test(trimmedContent));

      if (isMath) {
        // 是数学表达式，标记为数学公式
        return `<span class="math-inline-placeholder" data-formula="${trimmedContent}">${trimmedContent}</span>`;
      } else {
        // 是普通代码，保持原样
        return `<code class="inline-code">${trimmedContent}</code>`;
      }
    });

    // 🔧 处理混合文本中的数学表达式（改进版）
    // 检测文本中是否包含数学表达式，并智能分割处理
    if (!processed.includes('<')) {
      // 检测是否包含数学符号
      const mathPatterns = [
        /\\[a-zA-Z]+/, // LaTeX命令
        /\b[a-zA-Z]+\s*[=≠<>≤≥]\s*/, // 变量等式
        /[α-ωΑ-Ω]/, // 希腊字母
        /[∑∫∏∂∇∆]/, // 数学符号
        /\^[0-9]+|\^{[^}]+}/, // 上标
        /_{[^}]+}/, // 下标
        /\brac\{|\bfrac\{/, // 分数（包括破损的）
      ];

      const hasMath = mathPatterns.some(pattern => pattern.test(processed));

      if (hasMath) {
        // 如果整个文本看起来像一个数学表达式，直接处理
        if (/^[^a-zA-Z]*\\[a-zA-Z]+/.test(processed.trim()) ||
          /^[a-zA-Z0-9+\-*/=\s\\{}^_()]+$/.test(processed.trim())) {
          const trimmedContent = processed.trim();
          return `<span class="math-inline-placeholder" data-formula="${trimmedContent}">${trimmedContent}</span>`;
        }

        // 否则，智能分割文本和数学表达式
        // 使用更精确的正则表达式来匹配数学表达式
        processed = processed.replace(
          /([a-zA-Z0-9+\-*/=\s\\{}^_()]+(?:rac\{[^}]*\}|\\[a-zA-Z]+\{[^}]*\}|[a-zA-Z0-9+\-*/=\s\\{}^_()]+))/g,
          (match) => {
            // 检查匹配的内容是否真的是数学表达式
            const isMathExpression = mathPatterns.some(pattern => pattern.test(match));
            if (isMathExpression && match.trim().length > 1) {
              return `<span class="math-inline-placeholder" data-formula="${match.trim()}">${match.trim()}</span>`;
            }
            return match;
          }
        );
      }
    }

    return processed;
  };

  useEffect(() => {
    const renderMath = async () => {
      if (!containerRef.current || !content) return;

      setIsLoading(true);
      setError(null);

      try {


        // 检查是否包含数学公式（支持多种格式）
        const hasMath = /\$.*?\$|\\\[[\s\S]*?\\\]|\\\(.*?\\\)|`[^`]*\\[a-zA-Z]+[^`]*`|\\[a-zA-Z]+/.test(content);

        if (!hasMath) {
          // 纯文本内容，保持原始换行符，让 CSS 处理
          containerRef.current.textContent = content;
          setIsLoading(false);
          return;
        }

        // 处理混合内容
        const processedContent = processContent(content);
        

        
        containerRef.current.innerHTML = processedContent;

        // 收集所有需要渲染的公式
        const formulasToRender: Array<{
          formula: string;
          element: HTMLElement;
          options: MathRenderOptions;
          needsDisplayModeCheck?: boolean;
        }> = [];

        // 智能处理行内数学公式
        const placeholders = containerRef.current.querySelectorAll('.math-inline-placeholder');

        for (const placeholder of placeholders) {
          const formula = placeholder.getAttribute('data-formula') || '';



          // 预处理元素 - 根据缓存的显示模式决定元素类型
          const cachedDisplayMode = mathRenderCache.getDisplayMode(formula);

          if (cachedDisplayMode !== null) {
            // 有缓存，直接设置元素类型
            if (cachedDisplayMode) {
              // 行内显示
              placeholder.className = 'math-inline-formula';
              formulasToRender.push({
                formula,
                element: placeholder as HTMLElement,
                options: { displayMode: false, ...options },
                needsDisplayModeCheck: false
              });
            } else {
              // 块级显示
              const blockDiv = document.createElement('div');
              blockDiv.className = 'math-inline-block';
              blockDiv.textContent = formula;
              placeholder.parentNode?.replaceChild(blockDiv, placeholder);
              formulasToRender.push({
                formula,
                element: blockDiv,
                options: { displayMode: false, ...options },
                needsDisplayModeCheck: false
              });
            }
          } else {
            // 无缓存，需要智能判断
            formulasToRender.push({
              formula,
              element: placeholder as HTMLElement,
              options: { displayMode: false, ...options },
              needsDisplayModeCheck: true
            });
          }
        }

        // 渲染块级数学公式
        const displayBlocks = containerRef.current.querySelectorAll('.math-display-block');

        for (const element of displayBlocks) {
          const formula = element.textContent || '';
          formulasToRender.push({
            formula,
            element: element as HTMLElement,
            options: { displayMode: true, ...options },
            needsDisplayModeCheck: false
          });
        }



        // 批量异步渲染所有公式 (每批10个)
        await renderFormulasInBatches(formulasToRender, 10);
      } catch (err) {
        console.warn('Content processing failed:', err);
        setError(err instanceof Error ? err.message : 'Processing failed');

        // 降级到显示原始内容（保持换行）
        if (containerRef.current) {
          containerRef.current.textContent = content;
        }
      } finally {
        setIsLoading(false);
      }
    };

    renderMath();
  }, [content, displayMode, options]);

  return (
    <div className={`math-renderer ${className}`}>
      {isLoading && (
        <span className="math-loading">Loading...</span>
      )}
      <div
        ref={containerRef}
        className={`math-content ${displayMode ? 'math-display' : 'math-inline'} ${className}`}
        style={{
          display: isLoading ? 'none' : 'block',
          whiteSpace: 'pre-wrap', // 保持换行和空格
          wordBreak: 'break-word', // 长单词换行
          overflowWrap: 'break-word', // 强制换行
          wordWrap: 'break-word', // 兼容性
          fontFamily: 'inherit', // 确保继承父容器字体
          lineHeight: 'inherit', // 确保继承父容器行高
          isolation: 'isolate', // CSS 隔离，防止样式泄露
          maxWidth: '100%', // 限制最大宽度
          width: '100%', // 占满容器
          boxSizing: 'border-box' // 包含边框和内边距
        }}
      />
      {error && (
        <div className="math-error-info" style={{ fontSize: '0.8em', color: '#666' }}>
          Render error: {error}
        </div>
      )}
    </div>
  );
};

export default MathRenderer;